/**
 * useSessionTimeout
 *
 * Frontend inactivity timer. Reads a timeout token from localStorage
 * (set by WorkspaceSettings → Security → Session timeout) and signs
 * the user out after that many minutes of no interaction.
 *
 * Detection is purely client-side — a determined user could clear the
 * key in dev tools. The eventual answer is a server-side cookie TTL
 * but this is a useful first step for "I want my admins to actually
 * see the timeout do something."
 *
 * Activity events tracked: mousemove, mousedown, keydown, scroll,
 * touchstart, wheel. Each resets the timer. A throttle keeps the
 * reset cheap during heavy interaction.
 *
 * The hook expects a logout callback so it can plug into whatever
 * auth-clear flow the host app already uses (clearAuth + redirect to
 * /login is what UserMenuDialog does).
 */

import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'pidly.sessionTimeout';

// Token → ms. 'never' resolves to 0 (timer disabled).
const TIMEOUT_MS = {
  never: 0,
  '1h': 60 * 60 * 1000,
  '8h': 8 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

export function getStoredSessionTimeoutMs() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    return TIMEOUT_MS[raw] || 0;
  } catch { return 0; }
}

export function setStoredSessionTimeout(token) {
  try {
    if (token === 'never' || !token) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, token);
    // Notify other tabs / the running session that the timeout
    // changed so they can recompute their idle deadline without a
    // page refresh.
    try { window.dispatchEvent(new Event('pidly:session-timeout-changed')); } catch {}
  } catch {}
}

export default function useSessionTimeout(onTimeout) {
  // Latest activity timestamp (ms). Updated on user interaction.
  const lastActivityRef = useRef(Date.now());
  // Currently-armed setTimeout id, if any.
  const timerRef = useRef(null);
  // Last reset throttle stamp — we don't need to reset on every
  // mousemove pixel; once per second is plenty.
  const lastResetRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    const arm = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      const ms = getStoredSessionTimeoutMs();
      if (!ms) return; // 'never' / unset → no timer
      const deadline = lastActivityRef.current + ms;
      const wait = Math.max(1000, deadline - Date.now());
      timerRef.current = setTimeout(() => {
        // Re-check at fire time — if activity happened during the wait
        // we'll re-arm; otherwise fire the logout callback.
        const now = Date.now();
        if (now - lastActivityRef.current >= ms) {
          try { onTimeoutRef.current?.(); } catch (err) { console.warn('[session timeout] logout callback threw', err); }
        } else {
          arm();
        }
      }, wait);
    };

    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      // Throttle: only re-arm at most once per second.
      if (now - lastResetRef.current < 1000) return;
      lastResetRef.current = now;
      arm();
    };

    const onSettingChange = () => arm();

    arm();
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    events.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('pidly:session-timeout-changed', onSettingChange);
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      events.forEach(ev => window.removeEventListener(ev, onActivity));
      window.removeEventListener('pidly:session-timeout-changed', onSettingChange);
    };
  }, []);
}
